import { addDoc, collection, doc, runTransaction, serverTimestamp, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';
import {
  CampaignLaunchPreconditionError,
  commitGuardedFirestoreTransaction,
} from './guardedFirestoreTransaction.mjs';

export { CampaignLaunchPreconditionError };

function normalizeRecipients(value) {
  if (Array.isArray(value)) {
    return value
      .flatMap(item => Array.isArray(item) ? item : [item])
      .map(email => {
        if (email && typeof email === 'object') {
          return email.email || email.address || email.emailAddress?.address || '';
        }
        return email;
      })
      .map(email => String(email || '').trim())
      .filter(Boolean);
  }
  if (value && typeof value === 'object') {
    const address = value.email || value.address || value.emailAddress?.address || '';
    return address ? [String(address).trim()].filter(Boolean) : [];
  }
  const single = String(value || '').trim();
  return single ? [single] : [];
}

export async function createActionAuditEntry({
  uid,
  actionType,
  client,
  notification,
  thread,
  subject = '',
  suggestedBody,
  finalBody = '',
  suggestedRecipients,
  finalRecipients,
  suggestedCcRecipients,
  finalCcRecipients,
  rowAnchor,
  rowNumber,
  threadId,
  replyToMessageId,
  sourceMessageId,
  sourceGraphMessageId,
  sourceInternetMessageId,
  sourceMessage,
  source = 'dashboard',
  reason,
  status = 'queued',
  handledAt
}) {
  if (!uid) throw new Error('Cannot audit dashboard action without a signed-in user.');

  const auditPayload = buildActionAuditPayload({
    uid,
    actionType,
    client,
    notification,
    thread,
    subject,
    suggestedBody,
    finalBody,
    suggestedRecipients,
    finalRecipients,
    suggestedCcRecipients,
    finalCcRecipients,
    rowAnchor,
    rowNumber,
    threadId,
    replyToMessageId,
    sourceMessageId,
    sourceGraphMessageId,
    sourceInternetMessageId,
    sourceMessage,
    source,
    reason,
    status,
    handledAt
  });

  return addDoc(collection(db, 'users', uid, 'actionAudit'), auditPayload);
}

export function buildActionAuditPayload({
  uid,
  actionType,
  client,
  notification,
  thread,
  subject = '',
  suggestedBody,
  finalBody = '',
  suggestedRecipients,
  finalRecipients,
  suggestedCcRecipients,
  finalCcRecipients,
  rowAnchor,
  rowNumber,
  threadId,
  replyToMessageId,
  sourceMessageId,
  sourceGraphMessageId,
  sourceInternetMessageId,
  sourceMessage,
  source = 'dashboard',
  reason,
  status = 'queued',
  handledAt
}) {
  if (!uid) throw new Error('Cannot audit dashboard action without a signed-in user.');

  const suggestedEmail = notification?.meta?.suggestedEmail || {};
  const auditPayload = {
    actorUid: uid,
    actionType,
    status,
    source,
    clientId: client?.id || null,
    clientName: client?.name || client?.clientName || client?.companyName || null,
    notificationId: notification?.id || null,
    threadId: threadId || notification?.threadId || thread?.threadIds?.[0] || thread?.id || null,
    rowAnchor: rowAnchor ?? notification?.rowAnchor ?? null,
    reason: reason ?? notification?.meta?.reason ?? null,
    subject: subject || suggestedEmail.subject || '',
    suggestedBody: suggestedBody ?? suggestedEmail.body ?? '',
    finalBody,
    suggestedRecipients: normalizeRecipients(suggestedRecipients ?? suggestedEmail.to),
    finalRecipients: normalizeRecipients(finalRecipients),
    suggestedCcRecipients: normalizeRecipients(
      suggestedCcRecipients ??
      notification?.meta?.ccEmails ??
      notification?.meta?.ccRecipients ??
      suggestedEmail.cc ??
      suggestedEmail.ccEmails ??
      suggestedEmail.ccRecipients
    ),
    finalCcRecipients: normalizeRecipients(finalCcRecipients),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  if (rowNumber !== undefined && rowNumber !== null) {
    auditPayload.rowNumber = rowNumber;
  }

  // Mark Handled terminalizes an item without sending anything. firestore.rules
  // accepts an actionAudit create only with status queued/dismissed/stopped/
  // completed, so the terminal status is "completed" and handledAt - a key the
  // rules already permit - is what says the advisor closed it by hand.
  if (handledAt !== undefined && handledAt !== null) {
    auditPayload.handledAt = handledAt;
  }

  const sourceIdentity = {
    replyToMessageId,
    sourceMessageId,
    sourceGraphMessageId,
    sourceInternetMessageId,
    sourceMessage
  };
  Object.entries(sourceIdentity).forEach(([key, value]) => {
    if (value !== undefined && value !== null) auditPayload[key] = value;
  });

  return auditPayload;
}

export function prepareActionAuditOutboxBatch({ uid, entries = [], clientUpdate = null }) {
  if (!uid) throw new Error('Cannot queue dashboard action without a signed-in user.');
  if (clientUpdate && (!clientUpdate.clientId || !clientUpdate.data)) {
    throw new Error('Atomic campaign update requires clientId and data.');
  }

  const prepared = entries.map(entry => {
    const actionAuditRef = doc(collection(db, 'users', uid, 'actionAudit'));
    const outboxRef = doc(collection(db, 'users', uid, 'outbox'));
    const auditPayload = buildActionAuditPayload({ uid, ...(entry.audit || {}) });
    const outboxPayload = {
      ...(entry.outbox || {}),
      actionType: auditPayload.actionType,
      source: auditPayload.source,
      actionAuditId: actionAuditRef.id
    };

    return { actionAuditRef, outboxRef, auditPayload, outboxPayload };
  });
  const clientRef = clientUpdate
    ? doc(db, 'users', uid, 'clients', clientUpdate.clientId)
    : null;

  return {
    refs: prepared.map(({ actionAuditRef, outboxRef }) => ({ actionAuditRef, outboxRef })),
    async commit() {
      if (clientUpdate?.validateCurrent) {
        return commitGuardedFirestoreTransaction({
          db,
          runTransaction,
          guardRef: clientRef,
          validateCurrent: clientUpdate.validateCurrent,
          applyWrites: transaction => {
            transaction.update(clientRef, clientUpdate.data);
            prepared.forEach(({ actionAuditRef, outboxRef, auditPayload, outboxPayload }) => {
              transaction.set(actionAuditRef, auditPayload);
              transaction.set(outboxRef, outboxPayload);
            });
            return prepared.map(({ actionAuditRef, outboxRef }) => ({ actionAuditRef, outboxRef }));
          },
        });
      }

      const batch = writeBatch(db);
      if (clientUpdate) {
        batch.update(clientRef, clientUpdate.data);
      }
      prepared.forEach(({ actionAuditRef, outboxRef, auditPayload, outboxPayload }) => {
        batch.set(actionAuditRef, auditPayload);
        batch.set(outboxRef, outboxPayload);
      });
      await batch.commit();
      return prepared.map(({ actionAuditRef, outboxRef }) => ({ actionAuditRef, outboxRef }));
    }
  };
}
